/**
 * Soft form-events (`_changed`, `_custom`, …) — Swing `SwingForm.processEvent0`.
 * Канал WS: `/client/event/form/{coreId}` (не app / onServerEvent).
 */

import { t } from '@/common/utils/translations'

export type FormMessageType = 'info' | 'alert' | 'error'

export type FormMessageActionType = 'close_form' | 'reload_form' | 'hide_message'

export const FORM_SOFT_EVENT_TYPES = [
  '_changed',
  '_changedState',
  '_deleted',
  '_disconnected',
  '_reconnected',
  '_custom',
] as const

export type FormSoftEventType = (typeof FORM_SOFT_EVENT_TYPES)[number]

export const FORM_SOFT_SUBMIT_AUTO = 'auto'

export type FormSoftMessage = {
  id: string
  type: FormMessageType
  text: string
  actions: FormMessageActionType[]
  date: Date
  eventId: string | null
  eventType: FormSoftEventType
  coreId: string
  slotId: string
  /** Registry form id слота (для reloadForm). */
  formId?: string
  /** `_param.submit=auto` → сразу эмулировать reload_form (без balloon). */
  submitAuto?: boolean
  /** Обратный отсчёт перед авто-действием (мс). */
  autoCountdownMs?: number
  autoCountdownAction?: FormMessageActionType
}

const ACTION_I18N_KEYS: Record<FormMessageActionType, string> = {
  close_form: 'formMessages.actions.close_form',
  reload_form: 'formMessages.actions.reload_form',
  hide_message: 'formMessages.actions.hide_message',
}

export function isFormSoftEventType(value: unknown): value is FormSoftEventType {
  return typeof value === 'string' && (FORM_SOFT_EVENT_TYPES as readonly string[]).includes(value)
}

export function formMessageActionLabel(action: FormMessageActionType): string {
  return t(ACTION_I18N_KEYS[action])
}

function readField(data: Record<string, unknown> | undefined, key: string): unknown {
  if (!data) return undefined
  const entry = data[key]
  if (entry && typeof entry === 'object' && entry !== null && 'value' in entry) {
    return (entry as { value: unknown }).value
  }
  return entry
}

function readFieldAsString(
  data: Record<string, unknown> | undefined,
  key: string,
  fallback = '',
): string {
  const value = readField(data, key)
  if (value === undefined || value === null) return fallback
  return String(value)
}

function readEventTime(data: Record<string, unknown> | undefined): Date {
  const raw = readField(data, '_eventTime')
  if (raw instanceof Date) return raw
  if (typeof raw === 'number' && Number.isFinite(raw)) return new Date(raw)
  if (typeof raw === 'string' && raw.trim()) {
    const parsed = new Date(raw)
    if (!Number.isNaN(parsed.getTime())) return parsed
  }
  return new Date()
}

function formatEventTime(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

function readMessageActions(data: Record<string, unknown> | undefined): FormMessageActionType[] {
  let table = readField(data, '_message.actions')
  if (table && typeof table === 'object' && !Array.isArray(table) && 'value' in table) {
    table = (table as { value: unknown }).value
  }
  if (!Array.isArray(table) || table.length === 0) {
    return ['hide_message']
  }
  const actions: FormMessageActionType[] = []
  for (const row of table) {
    if (!row || typeof row !== 'object') continue
    const rowRecord = row as Record<string, unknown>
    const rowFields =
      (rowRecord.fields && typeof rowRecord.fields === 'object'
        ? (rowRecord.fields as Record<string, unknown>)
        : rowRecord)
    const actionType = readFieldAsString(rowFields, 'actionType')
    if (
      actionType === 'close_form' ||
      actionType === 'reload_form' ||
      actionType === 'hide_message'
    ) {
      actions.push(actionType)
    }
  }
  return actions.length > 0 ? actions : ['hide_message']
}

function readMessageType(data: Record<string, unknown> | undefined): FormMessageType {
  const raw = readFieldAsString(data, '_message.type', 'alert')
  if (raw === 'info' || raw === 'alert' || raw === 'error') return raw
  return 'alert'
}

function readNestedSubmit(container: unknown): string {
  if (!container || typeof container !== 'object') return ''
  return readFieldAsString(container as Record<string, unknown>, 'submit')
}

/** `submit` на документе-param soft-event (`_param.submit` / вложенный param). */
export function readFormSoftParamSubmit(data: Record<string, unknown> | undefined): string {
  if (!data) return ''
  const flat = readFieldAsString(data, '_param.submit')
  if (flat) return flat
  const nested =
    readNestedSubmit(readField(data, '_param')) ||
    readNestedSubmit(data._param) ||
    readFieldAsString(data, 'param.submit') ||
    readNestedSubmit(readField(data, 'param')) ||
    readNestedSubmit(data.param)
  return nested
}

export function isFormSoftSubmitAuto(value: unknown): boolean {
  return String(value ?? '').trim().toLowerCase() === FORM_SOFT_SUBMIT_AUTO
}

export function shouldAutoExecuteFormSoftReload(message: FormSoftMessage | null): boolean {
  if (!message?.submitAuto) return false
  return message.actions.includes('reload_form')
}

export type FormSoftParamIdentity = {
  _id: number
  _typeId: string
}

function readIdentityLong(data: Record<string, unknown> | undefined, key: string): number | null {
  const raw = readField(data, key)
  if (raw == null || raw === '') return null
  const num = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(num) ? num : null
}

/** Identity документа-param (`_param._id` / `_param._typeId`) для роутинга на dialog slot. */
export function readFormSoftParamIdentity(
  data: Record<string, unknown> | undefined,
): FormSoftParamIdentity | null {
  if (!data) return null
  const nested = (readField(data, '_param') ?? data._param) as Record<string, unknown> | undefined
  const id = readIdentityLong(data, '_param._id') ?? readIdentityLong(nested, '_id')
  const typeId = (
    readFieldAsString(data, '_param._typeId') || readFieldAsString(nested, '_typeId')
  ).trim()
  if (id == null || id < 0 || !typeId) return null
  return { _id: id, _typeId: typeId }
}

/**
 * Map UniData soft-event fields → FormSoftMessage (без slotId — его ставит роутер).
 */
export function mapSoftEventToMessage(input: {
  eventType: string
  data?: Record<string, unknown>
  coreId: string
  slotId: string
  formId?: string
}): FormSoftMessage | null {
  if (!isFormSoftEventType(input.eventType)) return null

  const data = input.data
  const eventTime = readEventTime(data)
  const eventIdRaw = readField(data, '_eventId')
  const eventId =
    eventIdRaw === undefined || eventIdRaw === null || String(eventIdRaw).trim() === ''
      ? null
      : String(eventIdRaw)
  const userNameRaw = readField(data, '_user.reverseShortName')
  const userName =
    userNameRaw === undefined || userNameRaw === null
      ? ''
      : String(userNameRaw).trim()
  const changerLine = userName
    ? `${t('formMessages.changedBy', { name: userName })}\n`
    : ''
  const timeLine = formatEventTime(eventTime)

  let type: FormMessageType = 'alert'
  let text = ''
  let actions: FormMessageActionType[] = ['hide_message']

  switch (input.eventType) {
    case '_changed':
      text = `${t('formMessages.documentEdited')}\n${changerLine}\n${timeLine}`
      actions = ['reload_form', 'hide_message']
      break
    case '_changedState':
      text = `${t('formMessages.documentStateChanged')}\n${changerLine}\n${timeLine}`
      actions = ['reload_form', 'hide_message']
      break
    case '_deleted':
      text = `${t('formMessages.documentDeleted')}\n${changerLine}\n${timeLine}`
      actions = ['close_form']
      break
    case '_disconnected':
      text = t('formMessages.formStale')
      actions = ['close_form', 'hide_message']
      break
    case '_reconnected':
      text = t('formMessages.connectionProblems')
      actions = ['close_form', 'hide_message']
      break
    case '_custom': {
      type = readMessageType(data)
      const customMessage = readFieldAsString(data, '_message.message', '')
      text = `${customMessage ? `${customMessage}\n` : ''}${timeLine}`
      actions = readMessageActions(data)
      break
    }
  }

  return {
    id: eventId ?? `${input.coreId}:${input.eventType}:${eventTime.getTime()}:${Math.random().toString(36).slice(2, 8)}`,
    type,
    text,
    actions,
    date: eventTime,
    eventId,
    eventType: input.eventType,
    coreId: input.coreId,
    slotId: input.slotId,
    formId: input.formId,
    submitAuto: isFormSoftSubmitAuto(readFormSoftParamSubmit(data)),
  }
}
